// EnquiryPrint.js
import {
  loadPdfImages,
  getPdfHeader,
  getPdfFooter,
  PDF_PAGE_MARGINS,
  createAndOpenPdf,
} from "../../../utility/pdfUtils";

// ─── Reusable table layout ───
const tableLayout = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  paddingTop: () => 5,
  paddingBottom: () => 5,
  paddingLeft: () => 5,
  paddingRight: () => 5,
};

// ─── Main Export ───
export const handlePrint = async (enquiry, action = "open") => {
  // ✅ Single line — uses shared cached loader from pdfUtils
  const { headerBase64, footerBase64 } = await loadPdfImages();

  // ─── Format date helper ───
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ─── Format datetime helper ───
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ─── Customer Information Table ───
  const buildCustomerInfoTable = () => [
    [
      { text: "Contact Person", bold: true },
      { text: enquiry.customer_name || "-" },
      { text: "Company Name", bold: true },
      { text: enquiry.company_name || "-" },
    ],
    [
      { text: "Email", bold: true },
      { text: enquiry.email || "-" },
      { text: "Phone", bold: true },
      { text: enquiry.phone || "-" },
    ],
    [
      { text: "Address", bold: true },
      { text: enquiry.address || "-" },
      { text: "City", bold: true },
      { text: enquiry.city || "-" },
    ],
    [
      { text: "State", bold: true },
      { text: enquiry.state || "-" },
      { text: "Country", bold: true },
      { text: enquiry.country || "-" },
    ],
  ];

  // ─── Enquiry Details Table ───
  const buildEnquiryDetailsTable = () => [
    [
      { text: "Enquiry Type", bold: true },
      { text: enquiry.enquiry_source || "-", colSpan: 3 },
      {},
      {},
    ],
    [
      { text: "Follow Up Date", bold: true },
      { text: formatDate(enquiry.follow_up_date) },
      { text: "Created By", bold: true },
      { text: enquiry.created_by_name || "-" },
    ],
    [
      { text: "Created At", bold: true },
      { text: formatDateTime(enquiry.created_at) },
      { text: "Status", bold: true },
      { text: enquiry.status || "-" },
    ],
  ];

  // ─── Line Items Table (UPDATED FOR NEW FIELDS) ───
  const buildLineItemsTable = () => {
    const body = [
      [
        { text: "S.No.", bold: true, alignment: "center" },
        { text: "Voltage Ratio", bold: true, alignment: "center" },
        { text: "Rating Specification", bold: true, alignment: "left" },
        { text: "Winding Material", bold: true, alignment: "center" },
        { text: "Qty", bold: true, alignment: "center" },
        { text: "Est. Price", bold: true, alignment: "center" },
      ],
    ];

    if (enquiry.line_items && enquiry.line_items.length > 0) {
      enquiry.line_items.forEach((item, index) => {
        body.push([
          { text: (index + 1).toString(), alignment: "center" },
          { text: item.voltage_ratio || "-", alignment: "center" },
          { text: (item.rating_specification || item.description || "-").toUpperCase(), alignment: "left" },
          { text: item.winding_material || "-", alignment: "center" },
          { text: (item.quantity || 0).toString(), alignment: "center" },
          { text: item.price_per_unit ? `Rs ${item.price_per_unit}` : "-", alignment: "center" },
        ]);
      });
    } else {
      // ✅ 6 Columns ki wajah se colSpan 6 kar diya gaya hai
      body.push([
        {
          text: "No items available",
          colSpan: 6,
          alignment: "center",
          italics: true,
        },
        {}, {}, {}, {}, {},
      ]);
    }

    return body;
  };

  // ─── Notes List ───
  const buildNotesList = () => {
    if (enquiry.notes && enquiry.notes.length > 0) {
      return enquiry.notes.map((note, index) => ({
        text: `${index + 1}. ${note.note_text || "-"}`,
        margin: [0, 3, 0, 3],
      }));
    }
    return [{ text: "No notes available", italics: true }];
  };

  const docDefinition = {
    pageSize: "A4",

    // ✅ Shared margins from pdfUtils
    pageMargins: PDF_PAGE_MARGINS,

    defaultStyle: { fontSize: 10 },

    watermark: {
      text: "SIDDHARTH INDUSTRIES",
      color: "#000000",
      opacity: 0.03,
      bold: true,
      fontSize: 65,
      angle: -45,
    },

    // ✅ Shared header & footer — one liner each
    header: getPdfHeader(headerBase64),
    footer: getPdfFooter(footerBase64, true),

    content: [
      // ─── Title ───
      {
        text: "ENQUIRY DETAILS",
        fontSize: 16,
        bold: true,
        alignment: "center",
        margin: [0, 0, 0, 20],
      },

      // ─── Enquiry Information ───
      {
        text: "Enquiry Information:",
        fontSize: 12,
        bold: true,
        margin: [0, 0, 0, 10],
      },
      {
        table: {
          widths: ["20%", "30%", "20%", "30%"],
          body: buildEnquiryDetailsTable(),
        },
        layout: tableLayout,
        margin: [0, 0, 0, 20],
      },

      // ─── Customer Information ───
      {
        text: "Customer Information:",
        fontSize: 12,
        bold: true,
        margin: [0, 0, 0, 10],
      },
      {
        table: {
          widths: ["20%", "30%", "20%", "30%"],
          body: buildCustomerInfoTable(),
        },
        layout: tableLayout,
        margin: [0, 0, 0, 20],
      },

      // ─── Line Items (UPDATED WIDTHS) ───
      {
        text: "Enquiry Items:",
        fontSize: 12,
        bold: true,
        margin: [0, 0, 0, 10],
      },
      {
        table: {
          headerRows: 1,
          // ✅ Updated Column Widths (Total 100%)
          widths: ["8%", "22%", "28%", "17%", "10%", "15%"], 
          body: buildLineItemsTable(),
        },
        layout: tableLayout,
        margin: [0, 0, 0, 20],
      },

      // ─── Notes ───
      {
        text: "Notes:",
        fontSize: 12,
        bold: true,
        margin: [0, 0, 0, 5],
      },
      ...buildNotesList(),
    ],
  };

  // ✅ Shared PDF creator from pdfUtils
  createAndOpenPdf(
    docDefinition,
    action,
    `Enquiry_${enquiry?.enquiry_id || enquiry?.id || "export"}`
  );
};